import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { LogOut, Menu, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";

const LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/about", label: "About", id: "about" },
  { to: "/gallery", label: "Gallery", id: "gallery" },
  { to: "/contact", label: "Contact", id: "contact" },
];

export default function Nav() {
  const { user, isAdmin, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    toast("Logged out. The den is sealed.");
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled || open
          ? "border-b border-white/10 bg-[#0B0E17]/80 backdrop-blur-md"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-2.5">
          <Logo size={32} />
          <span className="font-display text-sm font-extrabold uppercase tracking-tight text-slate-100 sm:text-base">
            TheHusko<span className="text-husko-cyan">Biscuit</span>
            <span className="ml-1 font-mono text-[10px] font-medium text-husko-magenta">.com</span>
          </span>
        </Link>

        <div className="hidden items-center gap-1 md:flex">
          {LINKS.map((link) => (
            <NavLink
              key={link.id}
              to={link.to}
              data-testid={`nav-link-${link.id}`}
              className={({ isActive }) =>
                `rounded-full px-4 py-2 font-display text-sm font-bold uppercase tracking-wide transition-colors ${
                  isActive ? "bg-white/10 text-husko-cyan" : "text-slate-300 hover:text-husko-cyan"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
          {isAdmin && (
            <span className="ml-3 flex items-center gap-1.5 rounded-full border border-husko-cyan/40 bg-husko-cyan/10 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-husko-cyan">
              <ShieldCheck size={12} /> Admin
            </span>
          )}
          {user && (
            <button
              data-testid="nav-logout-button"
              onClick={handleLogout}
              className="ml-2 flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-bold uppercase tracking-wide text-slate-400 transition-colors hover:text-husko-magenta"
            >
              <LogOut size={14} /> Exit
            </button>
          )}
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="rounded-lg p-2 text-slate-200 hover:bg-white/10 md:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="overflow-hidden border-b border-white/10 bg-[#0B0E17]/95 backdrop-blur-md md:hidden"
          >
            <div className="flex flex-col gap-1 px-4 pb-4 pt-2">
              {LINKS.map((link) => (
                <NavLink
                  key={link.id}
                  to={link.to}
                  data-testid={`nav-mobile-link-${link.id}`}
                  className={({ isActive }) =>
                    `rounded-xl px-4 py-3 font-display text-base font-bold uppercase tracking-wide ${
                      isActive ? "bg-white/10 text-husko-cyan" : "text-slate-300"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              {isAdmin && (
                <span className="mt-1 flex items-center gap-1.5 px-4 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-husko-cyan">
                  <ShieldCheck size={12} /> Admin mode
                </span>
              )}
              {user && (
                <button
                  data-testid="nav-mobile-logout-button"
                  onClick={handleLogout}
                  className="mt-1 flex items-center gap-2 rounded-xl px-4 py-3 text-left text-sm font-bold uppercase tracking-wide text-slate-400"
                >
                  <LogOut size={14} /> Log out
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
